/* eslint-disable react/no-danger,react/no-unknown-property,import/no-unresolved */
/* eslint-disable react/forbid-prop-types */
import React, { useMemo, useEffect } from 'react';
import { Route, useRouteMatch, useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { DashboardLayout } from 'assets/components';

import { useMutation, useQuery } from 'react-query';
import Loans from '../../screens/Loans';
import useIsUsUser from '../../utilities/sharedHooks/usUser';

import AccountsRouter from '../AccountsRouter';
import CardRouter from '../CardRouter';
import { useSetting } from '../../utilities/sharedHooks/settings';
import { LOANS_ALL } from '../../screens/Loans/queries';
import { LOAN_STATUS } from '../../screens/Loans/constants';
import OTC from '../../screens/OTC';
import { MODAL_SCREENS } from './constants';
import OxygenYieldRouter from '../OxygenYieldRouter';
import { useUserAllowStakingFeature, useUserPersonalInfo } from '../../utilities/sharedHooks/user';
import { USER_DETAILS_UPDATE_KEY } from '../../utilities/sharedMutations';
import { USER_REGION_SETTINGS_QUERY_KEY } from '../../utilities/sharedQueries';
import { hideSupportWidgetWithoutLogout } from '../../utilities/helpers';
import StakingRouter from '../StakingRouter';
import { ICONS } from '../../components/Icon/constants';
import UserRouter from '../UserRouter';

const DashboardRouter = () => {
  const { t: translate } = useTranslation();
  const { path } = useRouteMatch();
  const [loansEnabled, isLoadingSettings] = useSetting('app_features', 'feature_allow_loans');
  const [newLoanEnabled, isLoadingNewLoan] = useSetting('app_features', 'feature_allow_new_loans');
  const isUSUser = useIsUsUser();
  const { pathname } = useLocation();
  const history = useHistory();

  const { data: userRegionSettings } = useQuery(USER_REGION_SETTINGS_QUERY_KEY);

  const [allowOTCFeature] = useSetting('app_features', 'feature_allow_otc');
  const [faqUrl] = useSetting('content_urls', 'content_url_faq');

  const isAllowStakingFeature = useUserAllowStakingFeature();

  const [isCustomerSupportEnabled] = useSetting(
    'app_features',
    'feature_allow_in_app_customer_support'
  );

  const { mutate: refetchUser } = useMutation(USER_DETAILS_UPDATE_KEY);

  const [allowOxygenYieldFeature] = useSetting('app_features', 'feature_allow_oxygen_yield');
  const { allowOxygenYield } = useUserPersonalInfo();

  const oxygenYieldDisabled = useMemo(
    () => !allowOxygenYieldFeature || !allowOxygenYield || !userRegionSettings?.can_access_yield,
    [allowOxygenYieldFeature, allowOxygenYield, userRegionSettings]
  );

  useEffect(() => {
    if (oxygenYieldDisabled && pathname.indexOf('oxygen_yield') > 0) {
      history.push(`${path}/accounts`);
    }
  }, [history, oxygenYieldDisabled, pathname, path]);

  useEffect(() => {
    if (isCustomerSupportEnabled && pathname.indexOf('dashboard/settings') < 0) {
      hideSupportWidgetWithoutLogout();
    }
  }, [isCustomerSupportEnabled, pathname]);

  const { data: loansData, isLoading: isLoadingLoans } = useQuery(LOANS_ALL);
  const activeLoans = useMemo(() => {
    if (!isLoadingLoans && loansData) {
      return loansData?.filter((loan) => loan.status === LOAN_STATUS.ACTIVE);
    }

    return [];
  }, [isLoadingLoans, loansData]);

  useEffect(() => {
    refetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loansTabStatus = useMemo(() => {
    const info = {
      disabled: true,
      loading: true,
    };

    if (!isLoadingSettings && loansEnabled) {
      if (!isLoadingNewLoan && !newLoanEnabled) {
        if (isLoadingLoans) {
          info.disabled = false;
          info.loading = true;
        } else if (activeLoans?.length > 0) {
          info.disabled = false;
          info.loading = false;
        } else {
          info.disabled = true;
          info.loading = false;
        }
      } else if (!isLoadingNewLoan && newLoanEnabled) {
        info.disabled = false;
        info.loading = false;
      }
    } else if (!isLoadingSettings && loansEnabled === false) {
      info.disabled = true;
      info.loading = false;
    }

    return info;
  }, [
    activeLoans,
    isLoadingLoans,
    isLoadingNewLoan,
    isLoadingSettings,
    loansEnabled,
    newLoanEnabled,
  ]);

  const showServicesTabForMobile = useMemo(() => {
    if (
      (!loansTabStatus.loading && !loansTabStatus.disabled) ||
      !oxygenYieldDisabled ||
      allowOTCFeature ||
      isAllowStakingFeature
    ) {
      return true;
    }
    return false;
  }, [loansTabStatus, oxygenYieldDisabled, allowOTCFeature, isAllowStakingFeature]);

  return (
    <DashboardLayout>
      <Route
        path={`${path}/card`}
        component={CardRouter}
        title={translate('card_tab_label')}
        icon="icon-card-outline"
        activeIcon="icon-card-filled"
        isLink
        disabled={false}
      />
      <Route
        path={`${path}/accounts`}
        component={AccountsRouter}
        title={translate('accounts_tab_label')}
        icon="icon-wallet"
        activeIcon="icon-wallet-filled"
        isLink
        disabled={false}
      />
      <Route
        path={`${path}/oxygen_yield`}
        component={OxygenYieldRouter}
        title={translate('oxygen_yield_tab_label')}
        disabled={oxygenYieldDisabled}
        className="d-none d-md-block"
      />
      <Route
        path={`${path}/loans`}
        component={Loans}
        title={translate(`${isUSUser ? 'loans_tab_label_US' : 'loans_tab_label'}`)}
        disabled={loansTabStatus.disabled}
        icon="icon-loan-outline"
        activeIcon="icon-loan-filled"
        className="d-none d-md-block"
      />
      <Route
        path={`${path}/otc`}
        component={OTC}
        title={translate('OTC_tab_label')}
        disabled={!allowOTCFeature}
        className="d-none d-md-block"
      />
      <Route
        path={`${path}/staking`}
        component={StakingRouter}
        title={translate('staking_tab_label')}
        disabled={!isAllowStakingFeature}
        className="d-none d-md-block"
      />
      <Route
        className="ms-auto d-none d-md-block"
        path={faqUrl}
        icon={ICONS.QUESTION_MARK}
        activeIcon={false}
        isExternal
        right
        tooltipLabel={translate('help_center_label')}
        tooltipTop="160%"
        tooltipLeft="50%"
        disabled={false}
      />
      <Route
        path={`${path}/settings`}
        component={UserRouter}
        icon={ICONS.USER_PROFILE}
        activeIcon={false}
        className="d-none d-md-block"
        isLink
        right
        tooltipLabel={translate('settings')}
        tooltipTop="160%"
        tooltipLeft="50%"
        disabled={false}
      />
      <Route
        path={`${path}/settings`}
        component={UserRouter}
        title={translate('settings_tab_label')}
        icon={false}
        image
        activeIcon={false}
        className="d-block d-md-none"
        isLink
        disabled={false}
      />
      {showServicesTabForMobile && (
        <div
          disabled={false}
          title={translate('services_tab_label')}
          className="d-block d-md-none"
          icon="icon-settings-filled"
          activeIcon="icon-settings-filled"
          modal={MODAL_SCREENS.SERVICES}
          isLink={false}
          services={['otc', 'loans', 'oxygen_yield', 'staking']}
        />
      )}
    </DashboardLayout>
  );
};

export default DashboardRouter;
